import { Box, Button, ButtonGroup, LinearProgress, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import SpiderChart from './SpiderChart';

interface CategoryStat {
    category: string;
    total_response: number;
    response_count: number;
    average_response: number;
}

interface ResponseGraphsProps {
    entity?: any; // Athlete entity object (can be nested entity.entity or direct)
    data?: CategoryStat[]; // Optional: pre-structured data (for backward compatibility)
    title?: string;
    height?: number;
    maxValue?: number;
}

export const AssessmentDomainMap: Record<string, { name: string; description: string; max_value: number; question_count: number }> = {
    category_performance_mindset: {
        name: 'Performance Mindset',
        description: 'Reflects how intentionally and strategically an athlete prepares for performance—setting process goals, establishing routines, focusing on controllable factors, and using visualization to optimize readiness.',
        max_value: 5,
        question_count: 6,
    },
    category_emotional_regulation: {
        name: 'Emotional Regulation',
        description: 'Represents an athlete\'s ability to recognize, manage, and recover from emotional stressors encountered in sport contexts.',
        max_value: 5,
        question_count: 6,
    },
    category_confidence: {
        name: 'Confidence',
        description: 'Captures self-belief and perceived capability to perform successfully, even in challenging or uncertain situations.',
        max_value: 5,
        question_count: 6,
    },
    category_resilience_motivation: {
        name: 'Resilience & Motivation',
        description: 'Combines perseverance, adaptability, and intrinsic drive—reflecting how athletes persist through adversity and stay engaged with long-term goals.',
        max_value: 5,
        question_count: 6,
    },
    category_concentration: {
        name: 'Concentration',
        description: 'Reflects the athlete\'s ability to direct and sustain attention on task-relevant cues while minimizing distractions.',
        max_value: 5,
        question_count: 6,
    },
    category_leadership: {
        name: 'Leadership',
        description: 'Encompasses initiative, accountability, and the ability to inspire or guide others through behavior and example.',
        max_value: 5,
        question_count: 6,
    },
    category_mental_wellbeing: {
        name: 'Mental Well-being',
        description: 'Reflects overall enjoyment, balance, and emotional health within sport participation.',
        max_value: 5,
        question_count: 8,
    },
    category_communication: {
        name: 'Communication',
        description: 'Represents interpersonal clarity, active listening, and constructive interaction within a team or group environment.',
        max_value: 5,
        question_count: 6,
    },
};

// Structure spider data from entity
const structureSpiderData = (entity: any): CategoryStat[] => {
    if (!entity) return [];

    // Handle both nested entity.entity and direct entity
    const entityData = entity?.entity || entity;

    if (!entityData) return [];

    const spiderData: CategoryStat[] = [];
    for (const [field_name, domainInfo] of Object.entries(AssessmentDomainMap)) {
        if (entityData[field_name]) {
            const score = entityData[field_name];
            const numScore = typeof score === 'number' ? score : (typeof score === 'string' ? parseFloat(score) : 0);
            if (!isNaN(numScore)) {
                spiderData.push({
                    category: domainInfo.name,
                    average_response: numScore,
                    response_count: domainInfo.question_count,
                    total_response: numScore * domainInfo.question_count
                });
            }
        }
    }

    return spiderData;
};

const ResponseGraphs: React.FC<ResponseGraphsProps> = ({
    entity,
    data,
    title = undefined,
    height = 250,
    maxValue = 5
}) => {
    const [viewMode, setViewMode] = useState<'spider' | 'linear'>('spider');

    // Structure data from entity if provided, otherwise use provided data
    const structuredData = useMemo(() => {
        if (data) {
            return data;
        }
        if (entity) {
            return structureSpiderData(entity);
        }
        return [];
    }, [entity, data]);

    if (!structuredData || structuredData.length === 0) {
        return (
            <Typography variant="h6" color="text.secondary">
                No data available for response graphs
            </Typography>
        );
    }

    // Get domain info from category name (handles both formatted names and field names)
    const getDomainInfo = (category: string) => {
        // First, try to find by formatted name
        for (const [fieldName, domainInfo] of Object.entries(AssessmentDomainMap)) {
            if (domainInfo.name === category) {
                return domainInfo;
            }
        }
        // If not found, try to find by field name (category_*)
        if (category.startsWith('category_')) {
            return AssessmentDomainMap[category];
        }
        // Try to match by converting category to field name format
        const fieldName = `category_${category.toLowerCase().replace(/\s+/g, '_')}`;
        return AssessmentDomainMap[fieldName];
    };

    // Format category name (handle both formatted and unformatted names)
    const formatCategoryName = (category: string): string => {
        const domainInfo = getDomainInfo(category);
        if (domainInfo) {
            return domainInfo.name;
        }
        // Fallback to original formatting if not found in map
        return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Get description for a category
    const getCategoryDescription = (category: string): string => {
        const domainInfo = getDomainInfo(category);
        return domainInfo?.description || '';
    };

    return (
        <Box>
            {title && (
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                    {title}
                </Typography>
            )}

            {/* Toggle Button Group */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ButtonGroup variant="outlined" aria-label="view mode toggle">
                    <Button
                        onClick={() => setViewMode('spider')}
                        variant={viewMode === 'spider' ? 'contained' : 'outlined'}
                    >
                        Spider Chart
                    </Button>
                    <Button
                        onClick={() => setViewMode('linear')}
                        variant={viewMode === 'linear' ? 'contained' : 'outlined'}
                    >
                        Linear Progress
                    </Button>
                </ButtonGroup>
            </Box>

            {/* Conditional Rendering Based on View Mode */}
            {viewMode === 'spider' ? (
                <SpiderChart data={structuredData} title={undefined} height={height} showLegend={false} />
            ) : (
                <Box>
                    {structuredData.map((item, index) => {
                        const domainInfo = getDomainInfo(item.category);
                        const formattedCategory = formatCategoryName(item.category);
                        const itemMaxValue = domainInfo?.max_value ?? maxValue;
                        const progressValue = (item.average_response / itemMaxValue) * 100;
                        const description = getCategoryDescription(item.category);

                        return (
                            <Box key={index} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        {formattedCategory}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.average_response.toFixed(2)} / {itemMaxValue}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(progressValue, 100)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 1,
                                        backgroundColor: 'action.disabledBackground',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 1,
                                        },
                                    }}
                                />
                                {description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        {description}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};

export default ResponseGraphs;
export type { CategoryStat };

